/** One owner for the external report URL, router params and entry decision. */
export const MARKET_REPORT_ROUTE = '/market-intelligence/reports/:workspaceId/:monitorId/:analysisVersion';
export const MARKET_MONITORS_ROUTE = '/market-intelligence';
export type MarketReportTarget = { workspaceId: string; monitorId: string; analysisVersion: string };
const uuid = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

export function parseMarketReportTarget(params: Partial<MarketReportTarget>): MarketReportTarget | null {
    const { workspaceId, monitorId, analysisVersion } = params;
    if (![workspaceId, monitorId, analysisVersion].every(value => typeof value === 'string' && uuid.test(value))) return null;
    return { workspaceId: workspaceId!.toLowerCase(), monitorId: monitorId!.toLowerCase(), analysisVersion: analysisVersion!.toLowerCase() };
}

export function buildMarketReportPath(target: MarketReportTarget): string {
    const parsed = parseMarketReportTarget(target);
    if (!parsed) throw new Error('Invalid report target');
    return `${MARKET_MONITORS_ROUTE}/reports/${parsed.workspaceId}/${parsed.monitorId}/${parsed.analysisVersion}`;
}

export function buildMarketReportSignInPath(target: MarketReportTarget): string {
    const params = new URLSearchParams({ redirect: `/admin${buildMarketReportPath(target)}` });
    return `/auth?${params.toString()}`;
}

export type ReportLinkAccess = {
    signedIn: boolean; profileLoading: boolean; workspaceLoading: boolean; workspaceError: boolean;
    workspaces: { id: string; name: string }[]; currentWorkspaceId: string | null;
    permissions: { status: 'idle' | 'loading' | 'ready' | 'error'; isOwner: boolean; isAdminRole: boolean };
};
export type ReportLinkState =
    | { kind: 'invalid' | 'loading' | 'sign-in' | 'workspace-error' | 'unavailable' | 'permission-error' | 'denied' }
    | { kind: 'switch' | 'ready'; workspace: { id: string; name: string } };

export function resolveReportLinkState(target: MarketReportTarget | null, access: ReportLinkAccess): ReportLinkState {
    if (!target) return { kind: 'invalid' };
    if (access.profileLoading) return { kind: 'loading' };
    if (!access.signedIn) return { kind: 'sign-in' };
    if (access.workspaceLoading) return { kind: 'loading' };
    if (access.workspaceError) return { kind: 'workspace-error' };
    const workspace = access.workspaces.find(item => item.id === target.workspaceId);
    if (!workspace) return { kind: 'unavailable' };
    if (access.currentWorkspaceId !== workspace.id) return { kind: 'switch', workspace };
    if (access.permissions.status === 'error' || access.permissions.status === 'idle') return { kind: 'permission-error' };
    if (access.permissions.status !== 'ready') return { kind: 'loading' };
    if (!access.permissions.isOwner && !access.permissions.isAdminRole) return { kind: 'denied' };
    return { kind: 'ready', workspace };
}
